import { useQuery } from "@apollo/client";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import { ReactNode, useState } from "react";
import {
  GetProfileOverviewQueryQuery,
  GetProfileOverviewQueryQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache, {
  TopCollaboratorsViewEnum,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import WorkHistoryCollaboratorGraph from "@apps/work-history/components/work-history-collaborator-graph";
import WorkHistoryCollaboratorOrbit from "@apps/work-history/components/work-history-collaborator-orbit";
import { currentOrganizationVar, leftSidePanelVar } from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { lastSeenStr } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";
import { getUrlUsingTypename } from "@helpers/helpers";

import getProfileOverviewQuery from "./graphql/get-profile-overview-query";

const COLLABORATOR_VIEWS = [
  { value: TopCollaboratorsViewEnum.list, label: "List" },
  { value: TopCollaboratorsViewEnum.orbit, label: "Orbit" },
];

const CollaboratorViewToggle = ({
  value,
  onChange,
}: {
  value: TopCollaboratorsViewEnum;
  onChange: (view: TopCollaboratorsViewEnum) => void;
}) => (
  <div className="flex rounded-md bg-gray-100 p-0.5" role="tablist">
    {COLLABORATOR_VIEWS.map((view) => (
      <button
        key={view.value}
        type="button"
        role="tab"
        aria-selected={value === view.value}
        onClick={() => onChange(view.value)}
        className={classNames(
          "rounded-sm px-2 py-0.5 text-2xs font-medium",
          value === view.value
            ? "bg-white text-gray-700 shadow-xs"
            : "text-gray-500 hover:text-gray-700",
        )}
      >
        {view.label}
      </button>
    ))}
  </div>
);

const DetailItem = ({
  label,
  children,
}: {
  label: string;
  children: string | ReactNode;
}) => {
  return (
    <div className="flex items-start gap-2 text-gray-600 text-sm">
      <div className="font-medium text-gray-500 w-24 shrink-0">{label}</div>
      <div>{children}</div>
    </div>
  );
};

const ProfileSidebarDetails = ({ profileId }: { profileId: number }) => {
  const label = useLabel();
  const link = useLink();
  const organization = currentOrganizationVar();
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  // Held locally so the view flips immediately, then written back so it sticks next time.
  const [collaboratorView, setCollaboratorView] =
    useState<TopCollaboratorsViewEnum>(
      uiPreferenceCache.topCollaboratorsView ?? TopCollaboratorsViewEnum.list,
    );

  const { data, loading } = useQuery<
    GetProfileOverviewQueryQuery,
    GetProfileOverviewQueryQueryVariables
  >(getProfileOverviewQuery, {
    variables: {
      userId: profileId || -1,
    },
    skip: !profileId,
  });
  const profile = data?.user;
  const roleStr =
    profile?.currentRoles.map((role) => role.title).join(", ") || "";
  const reports = profile?.directReports
    ? assertEdgesNonNull(profile.directReports)
    : [];
  // Already sorted and trimmed to the strongest few by the backend (TF-1588). This is work-history
  // data, so it follows the same feature flag as the Work History panel — summaries outlive the
  // flag being turned off, and the backend falls back to the last one at any age.
  const topCollaborators = !organization.featureFlags.workHistory
    ? []
    : (profile?.topCollaborators ?? []).flatMap((collaborator) =>
        collaborator.user ? [{ ...collaborator, user: collaborator.user }] : [],
      );

  if (loading) {
    return <Loading className="p-6">Loading details...</Loading>;
  }

  if (!profile) return null;

  return (
    <div>
      <Layout.SidebarSection>
        <div className="flex flex-col gap-3">
          <DetailItem label="Email">{profile.email}</DetailItem>
          <DetailItem label="Timezone">{profile.timezone}</DetailItem>
          {profile.lastSeenStr && (
            <DetailItem label="Last seen">
              {lastSeenStr[profile.lastSeenStr]}
            </DetailItem>
          )}
        </div>
      </Layout.SidebarSection>

      <Layout.SidebarSection>
        <div className="flex flex-col gap-3">
          {roleStr && <DetailItem label="Role">{roleStr}</DetailItem>}
          <DetailItem
            label={label("team", {
              pluralize: true,
              titleCase: true,
            })}
          >
            {profile.teamsList.length === 0
              ? `No ${label("team", { pluralize: true })}.`
              : profile.teamsList.map((team) => team.title).join(", ")}
          </DetailItem>
          <DetailItem label="Managers">
            {profile.managersList.length === 0
              ? "No managers."
              : profile.managersList.map((manager, i) => (
                  <span key={manager.id}>
                    {i > 0 && ", "}
                    <AppLink
                      to={getUrlUsingTypename(manager)}
                      className="hover:underline"
                    >
                      {manager.name}
                    </AppLink>
                  </span>
                ))}
          </DetailItem>
          <DetailItem label="Reports">
            {reports.length === 0
              ? "No direct reports."
              : reports.map((report, i) => (
                  <span key={report.id}>
                    {i > 0 && ", "}
                    <AppLink
                      to={getUrlUsingTypename(report)}
                      className="hover:underline"
                    >
                      {report.name}
                    </AppLink>
                  </span>
                ))}
          </DetailItem>
        </div>
      </Layout.SidebarSection>

      {topCollaborators.length > 0 && (
        <Layout.SidebarSection>
          <div className="mb-3 flex items-center justify-between gap-4">
            <div className="text-sm font-medium text-gray-500">
              Top collaborators
            </div>
            <CollaboratorViewToggle
              value={collaboratorView}
              onChange={(view) => {
                setCollaboratorView(view);
                saveUiPreference({ topCollaboratorsView: view });
              }}
            />
          </div>
          {collaboratorView === TopCollaboratorsViewEnum.orbit ? (
            <WorkHistoryCollaboratorOrbit
              collaborators={topCollaborators}
              subjectLabel={profile.firstName || "You"}
            />
          ) : (
            <WorkHistoryCollaboratorGraph
              collaborators={topCollaborators}
              avatarSize={8}
            />
          )}
        </Layout.SidebarSection>
      )}

      <Layout.SidebarSection>
        <Button
          onClick={() => {
            saveUiPreference({ leftNavigationSidebarExpanded: true });
            leftSidePanelVar("settings");
            link.redirect(`/org-chart?userId=${profile.id}`);
          }}
        >
          View in org chart
          <ExternalLinkIcon className="text-gray-400 h-4 w-4" />
        </Button>
      </Layout.SidebarSection>
    </div>
  );
};

export default ProfileSidebarDetails;
